<template>
  <v-dialog
    v-model="isOpened"
    max-width="400"
    overlay-opacity="0.67"
    content-class="lpu-medal-dialog-window"
    data-qa="lpu_medals_dialog"
  >
    <CoreSwiper
      v-if="isOpened && items?.length"
      class="lpu-medal-dialog"
      :initial-slide="initialSlide"
      :slides-per-view="1"
      :space-between="16"
      :has-pagination="false"
      :has-navigation="!isMobile && items.length > 1"
      equal-slides-height
    >
      <template #slides="{ slideAttrs }">
        <div
          v-for="item in items"
          :key="item.id"
          v-bind="slideAttrs"
        >
          <div class="lpu-medal-dialog__slide d-flex flex-column">
            <img
              class="lpu-medal-dialog__image"
              :src="item.src"
              :alt="item.alt"
            >
            <div class="lpu-medal-dialog__header">
              <v-btn
                icon
                class="lpu-medal-dialog__close"
                data-qa="lpu_medals_dialog_close"
                @click="isOpened = false"
              >
                <span class="ui-icon-close-not-a-circle icon-secondary--text" />
              </v-btn>
            </div>
            <div class="lpu-medal-dialog__body d-flex flex-column align-center">
              <p class="text-body-1 text--text text-center mb-6">
                {{ item.alt }}
              </p>
              <a
                class="d-inline-flex align-center primary--text text-body-2 mt-auto text-decoration-none"
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer"
                data-qa="lpu_medals_dialog_more"
              >
                <span class="ui-icon-circle-info ui-icon-fz-big mr-2" />
                {{ tStatic('Подробнее') }}
              </a>
            </div>
          </div>
        </div>
      </template>
    </CoreSwiper>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { LpuMedalItem } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuMedals/types'

type Emits = {
  (_event: 'input', _value: boolean): void
}

const props = withDefaults(defineProps<{
  value: boolean
  items?: LpuMedalItem[]
  initialSlide?: number
}>(), {
  items: () => [],
  initialSlide: 0,
})

const emit = defineEmits<Emits>()
const isMobile = useIsMobile()

const isOpened = computed({
  get() {
    return props.value
  },
  set(value: boolean) {
    emit('input', value)
  },
})
</script>

<style lang="scss">
.v-dialog.lpu-medal-dialog-window {
  overflow: visible;
  box-shadow: none;
  background: transparent;
  width: calc(100% - 16px);
  max-width: 400px;
}
</style>

<style scoped lang="scss">
@import 'www/themes/doctors/common/variables';

.lpu-medal-dialog {
  $nav-btn-size: 40px;

  :deep(.swiper-container) {
    padding-top: 8px;
  }

  :deep(.swiper-slide) {
    width: 100% !important;
    max-width: 100%;
  }

  :deep(.core-swiper:not(.core-swiper--ready) > .swiper-container) {
    min-height: 261px;
    height: 261px;
    visibility: hidden;
  }

  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    top: 50%;
    margin-top: 0;
    transform: translateY(-50%);
    background-color: var(--lpu-branding-color-primary, $ui-kit-primary) !important;
    color: $ui-kit-bg-gray-0 !important;
  }

  :deep(.swiper-button-prev) {
    left: -($nav-btn-size / 2);
    right: auto;
  }

  :deep(.swiper-button-next) {
    right: -($nav-btn-size / 2);
    left: auto;
  }

  // Скрыта, но ловит клики: иначе промах по пропавшей стрелке попадает в слайд
  :deep(.swiper-button-disabled) {
    opacity: 0 !important;
    pointer-events: auto !important;
    cursor: default;
  }

  &__slide {
    position: relative;
    min-height: 261px;
    background-color: $ui-kit-bg-gray-0;
    border-radius: $border-radius-lg;
    box-shadow: 0 20px 36px rgba(0, 0, 0, 0.25);
    box-sizing: border-box;
  }

  &__image {
    position: absolute;
    top: -6px;
    left: 50%;
    z-index: 1;
    width: 120px;
    height: 120px;
    transform: translateX(-50%);
    object-fit: contain;
  }

  &__header {
    flex-shrink: 0;
    height: 88px;
    padding: 16px;
    background-color: $ui-kit-bg-gray-40;
    border-radius: $border-radius-lg $border-radius-lg 0 0;
    box-sizing: border-box;
  }

  &__close {
    margin: -8px;
  }

  &__body {
    flex: 1 0 auto;
    padding: 42px 16px 16px;
  }
}
</style>
